import React, { useEffect } from 'react';
import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useAdicionarDuplicata, useAtualizarDuplicata } from '../../../../hooks/useNFe';
import { DuplicataNFe, AdicionarDuplicataRequest, AtualizarDuplicataRequest } from '../../../../types/nfe';
import { toDateInputValue, fromDateInputValue } from '../../../../utils/dateUtils';

interface ModalDuplicataNFeProps {
  show: boolean;
  onHide: () => void;
  nfeId: string;
  duplicata?: DuplicataNFe;
}

const ModalDuplicataNFe: React.FC<ModalDuplicataNFeProps> = ({ show, onHide, nfeId, duplicata }) => {
  const { showToast } = useToast();
  const isEditMode = !!duplicata;

  const adicionarMutation = useAdicionarDuplicata(nfeId);
  const atualizarMutation = useAtualizarDuplicata(nfeId, duplicata?.id || '');

  const mutation = isEditMode ? atualizarMutation : adicionarMutation;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AdicionarDuplicataRequest | AtualizarDuplicataRequest>({
    defaultValues: duplicata
      ? {
          numero: duplicata.numero,
          dataVencimento: toDateInputValue(duplicata.dataVencimento),
          valor: duplicata.valor,
        }
      : {
          numero: '',
          dataVencimento: '',
          valor: 0,
        },
  });

  // Reset form when duplicata or show changes
  useEffect(() => {
    if (show) {
      if (duplicata) {
        reset({
          numero: duplicata.numero,
          dataVencimento: toDateInputValue(duplicata.dataVencimento),
          valor: duplicata.valor,
        });
      } else {
        reset({
          numero: '',
          dataVencimento: '',
          valor: 0,
        });
      }
    }
  }, [show, duplicata, reset]);

  const onSubmit = async (data: AdicionarDuplicataRequest | AtualizarDuplicataRequest) => {
    try {
      // Convert date to ISO 8601 with timezone
      const payload = {
        ...data,
        dataVencimento: fromDateInputValue(data.dataVencimento),
      };

      await mutation.mutateAsync(payload);
      showToast(
        isEditMode ? 'Duplicata atualizada com sucesso' : 'Duplicata adicionada com sucesso',
        'success'
      );
      onHide();
    } catch (error: any) {
      console.error('Erro ao salvar duplicata:', error);
      // Error handled by API interceptor
    }
  };

  const handleClose = () => {
    if (!mutation.isPending) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="border-bottom border-opacity-25">
        <Modal.Title className="text-body-emphasis">
          {isEditMode ? 'Editar Duplicata' : 'Adicionar Duplicata'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {mutation.isError && (
            <Alert
              variant="danger"
              className="border border-danger border-opacity-25 bg-danger-subtle mb-3"
            >
              <div className="text-danger-emphasis">
                Erro ao salvar duplicata. Por favor, tente novamente.
              </div>
            </Alert>
          )}

          <Row className="g-3">
            {/* Número da Duplicata */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="text-body-secondary fw-semibold">
                  Número da Duplicata *
                </Form.Label>
                <Form.Control
                  type="text"
                  {...register('numero', { required: 'Número é obrigatório' })}
                  isInvalid={!!errors.numero}
                  disabled={mutation.isPending}
                  placeholder="001, 002, etc."
                />
                {errors.numero && (
                  <Form.Control.Feedback type="invalid">
                    {errors.numero.message}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-body-tertiary">
                  Identificação da parcela (ex: 001, 002, 003)
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Data de Vencimento */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="text-body-secondary fw-semibold">
                  Data de Vencimento *
                </Form.Label>
                <Form.Control
                  type="date"
                  {...register('dataVencimento', { required: 'Data é obrigatória' })}
                  isInvalid={!!errors.dataVencimento}
                  disabled={mutation.isPending}
                />
                {errors.dataVencimento && (
                  <Form.Control.Feedback type="invalid">
                    {errors.dataVencimento.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Valor */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="text-body-secondary fw-semibold">Valor (R$) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('valor', {
                    required: 'Valor é obrigatório',
                    min: { value: 0.01, message: 'Deve ser maior que zero' },
                  })}
                  isInvalid={!!errors.valor}
                  disabled={mutation.isPending}
                />
                {errors.valor && (
                  <Form.Control.Feedback type="invalid">
                    {errors.valor.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mt-3 mb-0">
            <div className="text-info-emphasis small">
              As duplicatas representam as parcelas de pagamento da NFe. A soma dos valores deve
              corresponder ao total da nota.
            </div>
          </Alert>
        </Modal.Body>

        <Modal.Footer className="border-top border-opacity-25">
          <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            loadingPosition="start"
          >
            {isEditMode ? 'Atualizar' : 'Adicionar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalDuplicataNFe;
